import React, { useEffect, useState, useContext } from 'react';
import { X, Loader2, ChevronDown, ArrowLeft, CheckCircle } from 'lucide-react';
import { authService } from '../../services/auth';
import { formatBrazilianPhoneNumber } from '../../utils/fieldCache';
import { ToastContext } from '../../App';
import { FEATURES } from '../../config/features';
const RegisterModal = ({
  isOpen,
  onClose,
  onLoginClick,
  pendingAnalysis = false
}) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    confirmed_email: '',
    password: '',
    passwordConfirmation: '',
    invite_code: '',
    phone: '',
    a_empresa_em_que_trabalho_e___: '',
    seu_cargo: '',
    numero_de_funcionarios_na_empresa_em_que_trabalho: '',
    area_de_atuacao: '',
    website: '',
    como_voce_ouviu_falar_sobre_a_conversion_: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [error, setError] = useState('');
  const [codeVerificationMessage, setCodeVerificationMessage] = useState('');
  const [isCodeVerified, setIsCodeVerified] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const {
    addToast
  } = useContext(ToastContext);
  useEffect(() => {
    if (isOpen) {
      // Reset form state when modal is opened
      setStep(1);
      setError('');
      setIsSuccess(false);
      setCodeVerificationMessage('');
      setIsCodeVerified(false);
      setFormData({
        firstname: '',
        lastname: '',
        email: '',
        confirmed_email: '',
        password: '',
        passwordConfirmation: '',
        invite_code: '',
        phone: '',
        a_empresa_em_que_trabalho_e___: '',
        seu_cargo: '',
        numero_de_funcionarios_na_empresa_em_que_trabalho: '',
        area_de_atuacao: '',
        website: '',
        como_voce_ouviu_falar_sobre_a_conversion_: ''
      });
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    if (name === 'phone') {
      // Format phone number as user types
      setFormData(prev => ({
        ...prev,
        [name]: formatBrazilianPhoneNumber(value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  const validateStep1 = () => {
    if (!formData.firstname.trim()) {
      setError('Nome é obrigatório');
      return false;
    }
    if (!formData.lastname.trim()) {
      setError('Sobrenome é obrigatório');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email é obrigatório');
      return false;
    }
    if (!formData.confirmed_email.trim()) {
      setError('Confirmação de email é obrigatória');
      return false;
    }
    if (formData.email !== formData.confirmed_email) {
      setError('Os emails não conferem');
      return false;
    }
    if (!formData.password.trim()) {
      setError('Senha é obrigatória');
      return false;
    }
    // Validação de senha completa
    if (formData.password.length < 8) {
      setError('A senha deve ter pelo menos 8 caracteres');
      return false;
    }
    if (!/[A-Za-z]/.test(formData.password)) {
      setError('Senha deve conter pelo menos uma letra');
      return false;
    }
    if (!/[0-9]/.test(formData.password)) {
      setError('Senha deve conter pelo menos um número');
      return false;
    }
    if (!/[^A-Za-z0-9]/.test(formData.password)) {
      setError('Senha deve conter pelo menos um símbolo');
      return false;
    }
    if (!formData.passwordConfirmation.trim()) {
      setError('Confirmação de senha é obrigatória');
      return false;
    }
    if (formData.password !== formData.passwordConfirmation) {
      setError('As senhas não conferem');
      return false;
    }
    // Only validate invite code if feature is enabled
    if (FEATURES.REQUIRE_INVITE_CODE && !formData.invite_code.trim()) {
      setError('Código de convite é obrigatório');
      return false;
    }
    return true;
  };
  const validateStep2 = () => {
    if (!formData.phone.trim()) {
      setError('Telefone é obrigatório');
      return false;
    }
    if (!formData.a_empresa_em_que_trabalho_e___) {
      setError('Segmento é obrigatório');
      return false;
    }
    if (!formData.seu_cargo) {
      setError('Cargo é obrigatório');
      return false;
    }
    if (!formData.numero_de_funcionarios_na_empresa_em_que_trabalho) {
      setError('Número de funcionários é obrigatório');
      return false;
    }
    if (!formData.area_de_atuacao) {
      setError('Área de atuação é obrigatória');
      return false;
    }
    if (!formData.website.trim()) {
      setError('URL do site é obrigatória');
      return false;
    }
    return true;
  };
  const verifyInviteCode = async () => {
    if (!formData.email.trim()) {
      setError('Email é obrigatório para verificar o código');
      return false;
    }
    if (!formData.invite_code.trim()) {
      setError('Código de convite é obrigatório');
      return false;
    }
    setIsVerifyingCode(true);
    setError('');
    setCodeVerificationMessage('');
    try {
      const response = await authService.verifyInviteCode(formData.email, formData.invite_code);
      if (response.valid) {
        setIsCodeVerified(true);
        setCodeVerificationMessage('Código válido!');
        return true;
      } else {
        setIsCodeVerified(false);
        setError('Código inválido');
        return false;
      }
    } catch (error) {
      setIsCodeVerified(false);
      // Always show "Código inválido" for any error related to code verification
      setError('Código inválido');
      return false;
    } finally {
      setIsVerifyingCode(false);
    }
  };
  const handleNextStep = async () => {
    // Validar campos básicos primeiro
    if (!validateStep1()) {
      return;
    }
    // Only verify code if feature is enabled and code is not yet verified
    if (FEATURES.REQUIRE_INVITE_CODE && !isCodeVerified) {
      setIsVerifyingCode(true);
      const isValid = await verifyInviteCode();
      setIsVerifyingCode(false);
      if (!isValid) {
        return; // Não avança se o código for inválido
      }
    }
    // Se tudo estiver ok, avançar para o próximo passo
    setError('');
    setStep(2);
  };
  const handleVerifyCode = async e => {
    e.preventDefault();
    await verifyInviteCode();
  };
  const handlePrevStep = () => {
    setStep(1);
    setError('');
  };
  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateStep2()) {
      return;
    }
    setError('');
    setIsLoading(true);
    try {
      // Prepare data for API
      const callbackUrl = `${window.location.origin}/verificar-email?token={{token}}`;
      // Format phone number - remove non-digits
      const phoneDigits = formData.phone.replace(/\D/g, '');
      // Ensure URL has protocol
      let website = formData.website.trim();
      if (website && !website.match(/^https?:\/\//)) {
        website = 'https://' + website;
      }
      // Map form fields to backend expected fields - keeping original values
      const dataToSubmit = {
        firstname: formData.firstname,
        lastname: formData.lastname,
        email: formData.email,
        phone: phoneDigits.startsWith('+55') ? phoneDigits : `+55${phoneDigits}`,
        website: website,
        como_voce_ouviu_falar_sobre_a_conversion_: formData.como_voce_ouviu_falar_sobre_a_conversion_ || '',
        a_empresa_em_que_trabalho_e___: formData.a_empresa_em_que_trabalho_e___,
        seu_cargo: formData.seu_cargo,
        numero_de_funcionarios_na_empresa_em_que_trabalho: formData.numero_de_funcionarios_na_empresa_em_que_trabalho,
        area_de_atuacao: formData.area_de_atuacao,
        password: formData.password,
        callbackUrl
      };
      // Only include invite_code if feature is enabled
      if (FEATURES.REQUIRE_INVITE_CODE) {
        dataToSubmit.invite_code = formData.invite_code;
      }
      // Register the user
      await authService.register(dataToSubmit);
      // Prepare data for CRM registration
      const crmData = {
        firstname: formData.firstname,
        lastname: formData.lastname,
        email: formData.email,
        phone: phoneDigits.startsWith('+55') ? phoneDigits : `+55${phoneDigits}`,
        website: website,
        como_voce_ouviu_falar_sobre_a_conversion_: formData.como_voce_ouviu_falar_sobre_a_conversion_ || '',
        a_empresa_em_que_trabalho_e___: formData.a_empresa_em_que_trabalho_e___,
        seu_cargo: formData.seu_cargo,
        numero_de_funcionarios_na_empresa_em_que_trabalho: formData.numero_de_funcionarios_na_empresa_em_que_trabalho,
        area_de_atuacao: formData.area_de_atuacao
      };
      // Only include invite_code if feature is enabled
      if (FEATURES.REQUIRE_INVITE_CODE) {
        crmData.invite_code = formData.invite_code;
      }
      // Send data to CRM (don't await this call to not block the user flow)
      authService.registerCRM(crmData);
      setIsLoading(false);
      setIsSuccess(true);
      addToast('success', 'Conta criada com sucesso! Verifique seu email para ativar.');
    } catch (error) {
      setIsLoading(false);
      // Handle validation errors
      if (error.response?.data?.details) {
        const details = error.response.data.details;
        const errorMessage = details.map(d => d.message).join(', ');
        setError(`Erro de validação: ${errorMessage}`);
      } else {
        setError(error.response?.data?.message || 'Erro ao criar conta. Verifique os dados e tente novamente.');
      }
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 overflow-y-auto" data-id="element-197" data-mp="l4zqnwkx20hc">
      <div className="relative w-full max-w-3xl p-6 mx-auto bg-white rounded-lg shadow-xl flex flex-col my-8" data-id="element-198" data-mp="c6edyi24lfoh">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700" aria-label="Close" disabled={isLoading} data-id="element-199" data-mp="3sboteyuwpmg6">
          <X size={20} data-id="element-200" data-mp="5x5hd8temjcv" />
        </button>
        {isSuccess ? <div className="py-8 text-center flex-grow flex flex-col items-center justify-center" data-id="element-201" data-mp="5yq9krs8xq7k">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center" data-id="element-202" data-mp="znfu3hpv6ew">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-203" data-mp="34e9efqg6y2bk">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-204" data-mp="3ebe3pz5c2dwl" />
              </svg>
            </div>
            <h2 className="mb-2 text-2xl font-bold text-gray-900" data-id="element-205" data-mp="tlyh7gd5dbcr">
              Conta criada com sucesso!
            </h2>
            <p className="mb-6 text-gray-600" data-id="element-206" data-mp="1183nculrlsoo">
              Enviamos um email de confirmação para{' '}
              <strong data-id="element-207" data-mp="r7kx9i3lxfyb">{formData.email}</strong>. Por favor, verifique sua caixa
              de entrada e clique no link para ativar sua conta.
            </p>
            <button onClick={onClose} className="px-6 py-2 text-white bg-[#1649ff] rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" data-id="element-208" data-mp="w768aqw1xere">
              Fechar
            </button>
          </div> : <div className="flex flex-col" data-id="element-209" data-mp="1a5w5s7ms9gga">
            {step === 2 && <div className="flex items-center mb-4" data-id="element-210" data-mp="1f4ql2zljkmvn">
                <button onClick={handlePrevStep} className="mr-2 text-gray-500 hover:text-gray-700" disabled={isLoading} data-id="element-211" data-mp="1f35qj9z893xs">
                  <ArrowLeft size={20} data-id="element-212" data-mp="27nw89s9vtack" />
                </button>
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-213" data-mp="2hgicd24569a9">
                  Só faltam estes dados
                </h2>
              </div>}
            {step === 1 && <h2 className="mb-4 text-2xl font-bold text-center text-gray-900" data-id="element-214" data-mp="28g2bm0es9g93">
                Precisamos de alguns dados seus
              </h2>}
            {step === 2 && <div className="w-full mb-6 bg-gray-200 rounded-full h-2.5" data-id="element-215" data-mp="1yj7arav1f3rn">
                <div className="bg-[#1649ff] h-2.5 rounded-full w-3/4" data-id="element-216" data-mp="1tsh6k0ycl6yj"></div>
              </div>}
            {pendingAnalysis && <div className="mb-4 p-3 bg-blue-50 text-blue-800 rounded-md" data-id="element-217" data-mp="1otnt28j29fyd">
                Crie uma conta para continuar com a análise da sua marca.
              </div>}
            {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md" data-id="element-218" data-mp="1frob9e0827ud">
                {error}
              </div>}
            {FEATURES.REQUIRE_INVITE_CODE && isCodeVerified && step === 1 && <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-md flex items-center" data-id="element-219" data-mp="3misxqg146gb7">
                <CheckCircle size={18} className="mr-2" data-id="element-220" data-mp="3rhw3y3styskr" />
                {codeVerificationMessage}
              </div>}
            {step === 1 ? <form data-id="element-221" data-mp="4a27okjrqq7o">
                <div className="space-y-4" data-id="element-222" data-mp="4aakydjszxje">
                  <div className="grid grid-cols-2 gap-4" data-id="element-223" data-mp="33qgzo5hhu16v">
                    <div data-id="element-224" data-mp="33s1snqo5tc2m">
                      <label htmlFor="register-firstname" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-225" data-mp="38pbgj1vwhfoc">
                        Nome *
                      </label>
                      <input type="text" id="register-firstname" name="firstname" value={formData.firstname} onChange={handleChange} placeholder="Seu nome" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} autoComplete="given-name" data-id="element-226" data-mp="3hieztqt88mqw" />
                    </div>
                    <div data-id="element-227" data-mp="2atpmdxs4pecc">
                      <label htmlFor="register-lastname" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-228" data-mp="2omxf5sry4fvm">
                        Sobrenome *
                      </label>
                      <input type="text" id="register-lastname" name="lastname" value={formData.lastname} onChange={handleChange} placeholder="Seu sobrenome" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} autoComplete="family-name" data-id="element-229" data-mp="2ykv2me1ze8n6" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-230" data-mp="364cm5rf86yjq">
                    <div data-id="element-231" data-mp="2s54m6wb8nmeh">
                      <label htmlFor="register-email" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-232" data-mp="2s4lcg2vqs4bj">
                        E-mail corporativo *
                      </label>
                      <input type="email" id="register-email" name="email" value={formData.email} onChange={handleChange} placeholder="exemplo@empresa.com.br" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} autoComplete="email" data-id="element-233" data-mp="2i91i3kwttdow" />
                    </div>
                    <div data-id="element-234" data-mp="50h6ecj9cfht">
                      <label htmlFor="register-confirmed-email" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-235" data-mp="51ad5di7v4zy">
                        Confirmar e-mail *
                      </label>
                      <input type="email" id="register-confirmed-email" name="confirmed_email" value={formData.confirmed_email} onChange={handleChange} placeholder="exemplo@empresa.com.br" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} autoComplete="email" data-id="element-236" data-mp="sxwjtgibrrkb" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-237" data-mp="1xzfmulapnxcx">
                    <div data-id="element-238" data-mp="22xh5540bbsnc">
                      <label htmlFor="register-password" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-239" data-mp="27v8wnwx1pcr0">
                        Senha *
                      </label>
                      <input type="password" id="register-password" name="password" value={formData.password} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} minLength={8} autoComplete="new-password" data-id="element-240" data-mp="2gom19w4h22sx" />
                      <p className="mt-1 text-xs text-gray-500" data-id="element-241" data-mp="3pv6ppdnrpf1k">
                        Mínimo de 8 caracteres, incluindo pelo menos 1 letra, 1
                        número e 1 símbolo
                      </p>
                    </div>
                    <div data-id="element-242" data-mp="2x5g98xyny12a">
                      <label htmlFor="register-passwordConfirmation" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-243" data-mp="2t6h4pegug6bd">
                        Confirmar senha *
                      </label>
                      <input type="password" id="register-passwordConfirmation" name="passwordConfirmation" value={formData.passwordConfirmation} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} minLength={8} autoComplete="new-password" data-id="element-244" data-mp="324syfpd0bk81" />
                    </div>
                  </div>
                  {/* Only show invite code field if feature is enabled */}
                  {FEATURES.REQUIRE_INVITE_CODE && <div className="grid grid-cols-2 gap-4" data-id="element-245" data-mp="ypz9nhfx2oej">
                      <div data-id="element-246" data-mp="yph8q7ib7m0a">
                        <label htmlFor="register-invite-code" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-247" data-mp="ysd71wy2kcp9">
                          Código de convite *
                        </label>
                        <input type="text" id="register-invite-code" name="invite_code" value={formData.invite_code} onChange={handleChange} placeholder="Digite seu código de convite" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading || isVerifyingCode || isCodeVerified} data-id="element-248" data-mp="3ogm6o71mr48q" />
                      </div>
                      <div className="flex items-end" data-id="element-249" data-mp="35y6mlhbhxr5q">
                        <button type="button" onClick={handleVerifyCode} className="w-full px-3 py-2 bg-white text-[#1649ff] border border-[#1649ff] rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-center" disabled={isLoading || isVerifyingCode || isCodeVerified || !formData.invite_code.trim()} data-id="element-250" data-mp="3azvy0y2ms6uc">
                          {isVerifyingCode ? <Loader2 size={16} className="mr-1 animate-spin" data-id="element-251" data-mp="31aqcrw52u6fw" /> : isCodeVerified ? <CheckCircle size={16} className="mr-1" data-id="element-252" data-mp="2wbm18yuojwme" /> : null}
                          {isVerifyingCode ? 'Verificando...' : isCodeVerified ? 'Verificado' : 'Verificar código'}
                        </button>
                      </div>
                    </div>}
                </div>
                <div className="mt-6" data-id="element-253" data-mp="2cq0lih4x5vmy">
                  <button type="button" onClick={handleNextStep} className="w-full py-2 text-white bg-[#1649ff] rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center" disabled={isLoading || isVerifyingCode} data-id="element-254" data-mp="2cr1xqoxo8o69">
                    {isVerifyingCode ? <>
                        <Loader2 size={18} className="mr-2 animate-spin" data-id="element-255" data-mp="30ejtswnyy8f1" />
                        Verificando código...
                      </> : 'Continuar'}
                  </button>
                  <div className="mt-4 text-center" data-id="element-256" data-mp="39eygo0gkzvkn">
                    <p className="text-sm text-gray-600" data-id="element-257" data-mp="1b0n57k2qhjwq">
                      Já tem uma conta?{' '}
                      <button onClick={onLoginClick} className="text-[#1649ff] hover:underline" disabled={isLoading} type="button" data-id="element-258" data-mp="1f0ylylrz702x">
                        Entrar
                      </button>
                    </p>
                  </div>
                </div>
              </form> : <form onSubmit={handleSubmit} data-id="element-259" data-mp="ts3zfyscpgdr">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-260" data-mp="tozqyp7uryha">
                  <div className="md:col-span-1" data-id="element-261" data-mp="1qgab35nox91j">
                    <label htmlFor="register-phone" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-262" data-mp="1lhgydfg89m2f">
                      Número de telefone *
                    </label>
                    <input type="tel" id="register-phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="(XX) XXXXX-XXXX" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} autoComplete="tel" data-id="element-263" data-mp="1ch0w29b80kz5" />
                  </div>
                  <div className="md:col-span-1" data-id="element-264" data-mp="1k09tfw9qnd1i">
                    <label htmlFor="register-website" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-265" data-mp="27pkpx1k22l9u">
                      URL do site *
                    </label>
                    <input type="text" id="register-website" name="website" value={formData.website} onChange={handleChange} placeholder="www.exemplo.com.br" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} autoComplete="url" data-id="element-266" data-mp="1ynv0205tnue6" />
                  </div>
                  <div data-id="element-267" data-mp="tp7t0e5q437d">
                    <label htmlFor="register-segment" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-268" data-mp="tnn6nbtkbqkl">
                      Segmento *
                    </label>
                    <div className="relative" data-id="element-269" data-mp="3s7vgvgbslfm3">
                      <select id="register-segment" name="a_empresa_em_que_trabalho_e___" value={formData.a_empresa_em_que_trabalho_e___} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] appearance-none text-gray-900" required disabled={isLoading} data-id="element-270" data-mp="3s96lfid2are9">
                        <option value="" disabled data-id="element-271" data-mp="1icu0u6m837kf">
                          Selecione o segmento
                        </option>
                        <option value="Agência / Consultoria" data-id="element-272" data-mp="1n4lkdx6yod12">
                          Agência / Consultoria
                        </option>
                        <option value="Agronegócio" data-id="element-273" data-mp="1r78ey5v0oc1y">Agronegócio</option>
                        <option value="Autoconhecimento" data-id="element-274" data-mp="ug6go6ta5h51">
                          Autoconhecimento
                        </option>
                        <option value="Automotiva" data-id="element-275" data-mp="zfswmo0htbhj">Automotiva</option>
                        <option value="Bens de Consumo / Alimentação" data-id="element-276" data-mp="13e038q18swf0">
                          Bens de Consumo / Alimentação
                        </option>
                        <option value="E-commerce" data-id="element-277" data-mp="2ev881cfswejp">E-commerce</option>
                        <option value="Educação" data-id="element-278" data-mp="2ex2q8izotsk3">Educação</option>
                        <option value="Empresa pública / ONGs" data-id="element-279" data-mp="2js8bdfl9n374">
                          Empresa pública / ONGs
                        </option>
                        <option value="Energia" data-id="element-280" data-mp="2oyx0hi9u748b">Energia</option>
                        <option value="Eventos" data-id="element-281" data-mp="2ozf3qyux2ipq">Eventos</option>
                        <option value="Financeiro" data-id="element-282" data-mp="2k6vg192im777">Financeiro</option>
                        <option value="Imobiliário" data-id="element-283" data-mp="2g6latymjrkte">Imobiliário</option>
                        <option value="Indústria" data-id="element-284" data-mp="2g48ufpwftiig">Indústria</option>
                        <option value="Infoprodutos" data-id="element-285" data-mp="2b81ovy7ye911">Infoprodutos</option>
                        <option value="Mídia comunicação" data-id="element-286" data-mp="267m970078pfw">
                          Mídia comunicação
                        </option>
                        <option value="Mobilidade" data-id="element-287" data-mp="219ks2kmc3072">Mobilidade</option>
                        <option value="Moda / Vestuário" data-id="element-288" data-mp="1x8938hbaaizx">
                          Moda / Vestuário
                        </option>
                        <option value="Religião / Espiritualidade" data-id="element-289" data-mp="3vmaopa436ni5">
                          Religião / Espiritualidade
                        </option>
                        <option value="Saúde / Beleza / Bem-Estar" data-id="element-290" data-mp="eocd9y8uhscy">
                          Saúde / Beleza / Bem-Estar
                        </option>
                        <option value="Serviços gerais" data-id="element-291" data-mp="isk1zvfaz8b6">Serviços gerais</option>
                        <option value="Tecnologia / SaaS" data-id="element-292" data-mp="4yjbfoyi8rlw">
                          Tecnologia / SaaS
                        </option>
                        <option value="Telecomunicações" data-id="element-293" data-mp="9vt195pt35a9">
                          Telecomunicações
                        </option>
                        <option value="Transporte / Logística" data-id="element-294" data-mp="80tc0dyd2m">
                          Transporte / Logística
                        </option>
                        <option value="Turismo / Lazer" data-id="element-295" data-mp="3i7nqzirepfkb">Turismo / Lazer</option>
                        <option value="Varejo" data-id="element-296" data-mp="3n57fdbhtj8pz">Varejo</option>
                        <option value="Outro" data-id="element-297" data-mp="1gdoif55o5cbb">Outro</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} data-id="element-298" data-mp="1ld11detf5z8m" />
                    </div>
                  </div>
                  <div data-id="element-299" data-mp="1qjpq3ge2vy9a">
                    <label htmlFor="register-cargo" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-300" data-mp="1qg1xcjggjfvm">
                      Seu cargo *
                    </label>
                    <div className="relative" data-id="element-301" data-mp="2e77kzksn3cgh">
                      <select id="register-cargo" name="seu_cargo" value={formData.seu_cargo} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] appearance-none text-gray-900" required disabled={isLoading} autoComplete="organization-title" data-id="element-302" data-mp="2e5w7uudb0scp">
                        <option value="" disabled data-id="element-303" data-mp="3tw44j14tazlb">
                          Selecione seu cargo
                        </option>
                        <option value="CEO" data-id="element-304" data-mp="3oylvlaqsokdb">CEO</option>
                        <option value="Fundador" data-id="element-305" data-mp="3k6kmfxoxb9ri">Fundador</option>
                        <option value="Presidente" data-id="element-306" data-mp="3k4rgkkm4wtkk">Presidente</option>
                        <option value="Diretor" data-id="element-307" data-mp="3f6g74azr4l2v">Diretor</option>
                        <option value="Gerente" data-id="element-308" data-mp="3b5mh9c2r0w7c">Gerente</option>
                        <option value="Coordenador" data-id="element-309" data-mp="3b77e58cjcr8d">Coordenador</option>
                        <option value="Analista" data-id="element-310" data-mp="365rthhe9smqi">Analista</option>
                        <option value="Consultor" data-id="element-311" data-mp="317gfcugxzq2h">Consultor</option>
                        <option value="Autônomo" data-id="element-312" data-mp="319sxb7mpa0fj">Autônomo</option>
                        <option value="Supervisor" data-id="element-313" data-mp="2w9x3203t1nd6">Supervisor</option>
                        <option value="Assessor" data-id="element-314" data-mp="zgvaptjio431">Assessor</option>
                        <option value="Assistente" data-id="element-315" data-mp="ze8znjxf36g2">Assistente</option>
                        <option value="Estagiário" data-id="element-316" data-mp="uhs3c2g2dlrw">Estagiário</option>
                        <option value="Estudante" data-id="element-317" data-mp="12z2hqx2t7alw">Estudante</option>
                        <option value="Professor" data-id="element-318" data-mp="16zus5tnf3ljr">Professor</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} data-id="element-319" data-mp="16xi3yyr2gey9" />
                    </div>
                  </div>
                  <div data-id="element-320" data-mp="k0ghfkf49nj1">
                    <label htmlFor="register-funcionarios" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-321" data-mp="k0zqebmsh1kx">
                      Número de funcionários *
                    </label>
                    <div className="relative" data-id="element-322" data-mp="xzplxmtymt76">
                      <select id="register-funcionarios" name="numero_de_funcionarios_na_empresa_em_que_trabalho" value={formData.numero_de_funcionarios_na_empresa_em_que_trabalho} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] appearance-none text-gray-900" required disabled={isLoading} data-id="element-323" data-mp="y0hhixnk4qxg">
                        <option value="" disabled data-id="element-324" data-mp="10zpqd9v1wxs6">
                          Selecione a quantidade
                        </option>
                        <option value="1 - 10" data-id="element-325" data-mp="w1ei33kemvc2">1 - 10</option>
                        <option value="11 - 20" data-id="element-326" data-mp="1apj168ndd8vd">11 - 20</option>
                        <option value="21 - 50" data-id="element-327" data-mp="1ar2g9vxahdvk">21 - 50</option>
                        <option value="51 - 100" data-id="element-328" data-mp="15yj515m2qutx">51 - 100</option>
                        <option value="101 - 200" data-id="element-329" data-mp="15wy8xbk433ty">101 - 200</option>
                        <option value="201 - 500" data-id="element-330" data-mp="i7niamwh6ptj">201 - 500</option>
                        <option value="501 - 1.000" data-id="element-331" data-mp="dax0nv50vm60">501 - 1.000</option>
                        <option value="1.001 - 5.000" data-id="element-332" data-mp="2oxgz1jkd3ggm">1.001 - 5.000</option>
                        <option value="Acima de 5.000" data-id="element-333" data-mp="2ove3yl4cqx51">Acima de 5.000</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} data-id="element-334" data-mp="2tq0gwsq30xr8" />
                    </div>
                  </div>
                  <div data-id="element-335" data-mp="37noxhod49iqh">
                    <label htmlFor="register-area" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-336" data-mp="37ohx85dnr7z4">
                      Área de atuação *
                    </label>
                    <div className="relative" data-id="element-337" data-mp="32ovkdx1qrtqu">
                      <select id="register-area" name="area_de_atuacao" value={formData.area_de_atuacao} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] appearance-none text-gray-900" required disabled={isLoading} data-id="element-338" data-mp="32nkftv0h2hyo">
                        <option value="" disabled data-id="element-339" data-mp="1xrtn0ft8t88t">
                          Selecione a área
                        </option>
                        <option value="Administrativo" data-id="element-340" data-mp="1spvv6x6s1549">Administrativo</option>
                        <option value="Comercial" data-id="element-341" data-mp="2qf7alnb1wrg9">Comercial</option>
                        <option value="Contábil" data-id="element-342" data-mp="2lgcsyootl8xq">Contábil</option>
                        <option value="Customer Success" data-id="element-343" data-mp="2let96o3ygfr7">
                          Customer Success
                        </option>
                        <option value="E-commerce" data-id="element-344" data-mp="2cgpqs0o9nz3m">E-commerce</option>
                        <option value="Financeiro" data-id="element-345" data-mp="152ebloxo1dcd">Financeiro</option>
                        <option value="Marketing" data-id="element-346" data-mp="153pfdor42l3h">Marketing</option>
                        <option value="Operação" data-id="element-347" data-mp="10zpy33y0bi92">Operação</option>
                        <option value="RH" data-id="element-348" data-mp="3coecu9sfm40u">RH</option>
                        <option value="SEO" data-id="element-349" data-mp="3cpphebtpbft0">SEO</option>
                        <option value="Tecnologia" data-id="element-350" data-mp="3gp7zz0y1veso">Tecnologia</option>
                        <option value="Outra área" data-id="element-351" data-mp="3gndd3h37xcly">Outra área</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} data-id="element-352" data-mp="3lmzpxpf4wqu8" />
                    </div>
                  </div>
                  <div className="md:col-span-2" data-id="element-353" data-mp="47jhrghvlnxb">
                    <label htmlFor="register-como-conheceu" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-354" data-mp="4a5rnms8ofip">
                      Como você ouviu falar sobre a Conversion?
                    </label>
                    <input type="text" id="register-como-conheceu" name="como_voce_ouviu_falar_sobre_a_conversion_" value={formData.como_voce_ouviu_falar_sobre_a_conversion_} onChange={handleChange} placeholder="Google, indicação, redes sociais, etc." className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" disabled={isLoading} data-id="element-355" data-mp="2a2ucfpna3u9r" />
                  </div>
                </div>
                <div className="mt-6 flex space-x-4" data-id="element-356" data-mp="5wxnvsz9824">
                  <button type="button" onClick={handlePrevStep} className="w-1/3 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2" disabled={isLoading} data-id="element-357" data-mp="55btcp6mop1">
                    Voltar
                  </button>
                  <button type="submit" className="w-2/3 py-2 text-white bg-[#1649ff] rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center" disabled={isLoading} data-id="element-358" data-mp="ixps7unp31by">
                    {isLoading ? <>
                        <Loader2 size={18} className="mr-2 animate-spin" data-id="element-359" data-mp="xhttrikrripv" />
                        Processando...
                      </> : 'Cadastre-se'}
                  </button>
                </div>
              </form>}
          </div>}
      </div>
    </div>;
};
export default RegisterModal;